import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showVerificationNotice, setShowVerificationNotice] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      toast.success('Bestätigungs-E-Mail wurde erneut gesendet');
    } catch (error) {
      toast.error('Fehler beim erneuten Senden. Bitte versuche es später erneut.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, user } = response.data;
      
      onLogin(user, access_token);
      toast.success('Erfolgreich angemeldet!');
      
      // Zurueck zur Seite, von der die Anmeldung ausgeloest wurde (z.B. Anfrage-CTA)
      const from = location.state?.from;
      if (from) {
        navigate(from, { replace: true });
        return;
      }

      // Redirect based on role
      if (user.role === 'provider') {
        navigate('/provider/dashboard');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'customer') {
        navigate('/customer/dashboard');
      } else {
        // Fallback for any other role
        navigate('/search');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Einheitliche deutsche Fehlermeldung für Login-Fehler
      if (error.response?.status === 403 && error.response?.data?.detail === 'email_not_verified') {
        setShowVerificationNotice(true);
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Dein Konto wurde gesperrt. Bitte kontaktiere den Support.');
      } else if (error.response?.status === 401) {
        toast.error('Anmeldung fehlgeschlagen. Bitte überprüfe E-Mail-Adresse und Passwort.');
      } else if (error.response?.status >= 500) {
        toast.error('Es ist ein Serverfehler aufgetreten. Bitte versuche es später erneut.');
      } else if (!error.response) {
        toast.error('Netzwerkfehler. Bitte überprüfe deine Internetverbindung.');
      } else {
        toast.error('Anmeldung fehlgeschlagen. Bitte überprüfe E-Mail-Adresse und Passwort.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#2B2142] mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
        
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2 text-gray-900">Anmelden</h1>
          <p className="text-gray-600 mb-8">
            Willkommen zurück bei EventConnect
          </p>
          {showVerificationNotice ? (
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    E-Mail noch nicht bestätigt
                  </p>
                  <p className="text-sm text-amber-700 mb-3">
                    Bitte klicke auf den Link in der Bestätigungs-E-Mail, um dein Konto zu aktivieren.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 hover:text-amber-900 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? 'Wird gesendet...' : 'E-Mail erneut senden'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-[#FFF0D1]/50 rounded-xl border border-[#2B2142]/20">
              <p className="text-sm text-[#2B2142]">
                Egal ob Nutzer oder Dienstleister – melde dich hier mit deiner E-Mail-Adresse und deinem Passwort an.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                E-Mail
              </label>
              <input
                type="email"
                data-testid="email-input"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="deine@email.de"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Passwort
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/passwort-vergessen')}
                  className="text-sm text-[#2B2142] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Passwort vergessen?
                </button>
              </div>
              <input
                type="password"
                data-testid="password-input"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="Dein Passwort"
              />
            </div>
            
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl disabled:opacity-50"
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Noch kein Konto?{' '}
              <button
                onClick={() => navigate('/register', { state: location.state })}
                className="text-[#2B2142] font-semibold hover:underline"
                data-testid="go-to-register"
              >
                Jetzt registrieren
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
